import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Star, Check, X, Trash2, ShieldCheck, Filter } from "lucide-react";
import AdminLayout from "../components/AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminReviews() {
  const [filter, setFilter] = useState("pending");
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/reviews`, {
        params: filter ? { status: filter } : {},
        withCredentials: true,
      });
      setReviews(data || []);
    } catch (e) {
      toast.error("Chargement impossible");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]);

  const act = async (id, action) => {
    try {
      if (action === "delete") {
        if (!window.confirm("Supprimer définitivement cet avis ?")) return;
        await axios.delete(`${API}/admin/reviews/${id}`, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/reviews/${id}/${action}`, {}, { withCredentials: true });
      }
      toast.success(action === "approve" ? "Avis approuvé" : action === "reject" ? "Avis rejeté" : "Avis supprimé");
      setReviews((prev) => prev.filter((r) => r.id !== id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action impossible");
    }
  };

  const counts = reviews.length;
  const statusOptions = [
    { key: "pending", label: "En attente" },
    { key: "approved", label: "Publiés" },
    { key: "rejected", label: "Rejetés" },
    { key: "awaiting_submission", label: "En attente client" },
  ];

  return (
    <AdminLayout current="reviews">
      <div className="max-w-6xl">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <p className="label-caps text-[color:var(--gold)] text-[10px] tracking-[0.3em] mb-2">Modération</p>
            <h1 className="font-serif text-4xl">Avis clients</h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap" data-testid="admin-review-filters">
            {statusOptions.map((s) => (
              <button
                key={s.key}
                data-testid={`filter-${s.key}`}
                onClick={() => setFilter(s.key)}
                className={`px-4 py-2 label-caps text-[10px] tracking-[0.25em] border transition-colors ${
                  filter === s.key
                    ? "border-[color:var(--gold)] text-[color:var(--gold)] bg-[color:var(--gold)]/5"
                    : "border-white/10 text-white/60 hover:text-white hover:border-white/30"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {loading && <p className="text-white/40 text-center py-12">Chargement…</p>}
        {!loading && reviews.length === 0 && (
          <div className="text-center py-20 border border-dashed border-white/10">
            <p className="text-white/50">Aucun avis dans cette catégorie.</p>
          </div>
        )}

        <div className="space-y-4" data-testid="admin-reviews-list">
          {reviews.map((r) => (
            <ReviewRow key={r.id} r={r} onAction={act} filter={filter} />
          ))}
        </div>
        {!loading && counts > 0 && <p className="text-white/40 text-xs mt-6">{counts} avis affichés</p>}
      </div>
    </AdminLayout>
  );
}

const ReviewRow = ({ r, onAction, filter }) => {
  const canModerate = r.status === "pending";
  return (
    <div className="border border-white/10 p-6 hover:border-white/25 transition-colors" data-testid={`review-row-${r.id.slice(0,8)}`}>
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="flex-1 min-w-[280px]">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <div className="flex">
              {[1,2,3,4,5].map((n) => (
                <Star key={n} size={16} className={n <= r.rating ? "text-[color:var(--gold)] fill-[color:var(--gold)]" : "text-white/20"} strokeWidth={1.5} />
              ))}
            </div>
            <span className="label-caps text-[10px] text-white/40">{r.customer_name}</span>
            <span className="inline-flex items-center gap-1 label-caps text-[9px] text-[color:var(--gold)] border border-[color:var(--gold)] px-2 py-0.5">
              <ShieldCheck size={10} /> Achat vérifié
            </span>
            {r.status === "approved" && <span className="label-caps text-[9px] text-emerald-400">✓ Publié</span>}
            {r.status === "rejected" && <span className="label-caps text-[9px] text-red-400">Rejeté</span>}
            {r.status === "awaiting_submission" && <span className="label-caps text-[9px] text-orange-400">Attente client</span>}
          </div>
          {r.title && <p className="font-serif text-xl mb-2">{r.title}</p>}
          <p className="text-white/70 text-sm leading-relaxed mb-3 whitespace-pre-wrap">{r.body || <em className="text-white/30">(pas encore soumis)</em>}</p>
          <p className="text-white/40 text-xs">
            Produit : <Link to={`/parfum/${r.product_slug}`} className="text-[color:var(--gold)] hover:underline">{r.product_name}</Link>
            {" · "}Commande #{r.order_id?.slice(0,8).toUpperCase()}
            {r.submitted_at && ` · Reçu le ${new Date(r.submitted_at).toLocaleDateString("fr-FR")}`}
          </p>
          {r.photos && r.photos.length > 0 && (
            <div className="flex gap-2 mt-3">
              {r.photos.map((u) => (
                <a key={u} href={`${process.env.REACT_APP_BACKEND_URL}${u}`} target="_blank" rel="noreferrer" className="w-20 h-20 block border border-white/15">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${u}`} alt="review" className="w-full h-full object-cover" />
                </a>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2 min-w-[130px]">
          {canModerate && (
            <>
              <button
                data-testid={`approve-${r.id.slice(0,8)}`}
                onClick={() => onAction(r.id, "approve")}
                className="inline-flex items-center justify-center gap-2 bg-[color:var(--gold)] text-black px-4 py-2 label-caps text-[10px] tracking-[0.25em] hover:bg-white transition-colors"
              >
                <Check size={14} /> Approuver
              </button>
              <button
                data-testid={`reject-${r.id.slice(0,8)}`}
                onClick={() => onAction(r.id, "reject")}
                className="inline-flex items-center justify-center gap-2 border border-white/20 text-white/70 hover:border-red-400 hover:text-red-400 px-4 py-2 label-caps text-[10px] tracking-[0.25em] transition-colors"
              >
                <X size={14} /> Rejeter
              </button>
            </>
          )}
          {(r.status === "approved" || r.status === "rejected") && (
            <button
              onClick={() => onAction(r.id, "delete")}
              className="inline-flex items-center justify-center gap-2 border border-white/15 text-white/50 hover:border-red-400 hover:text-red-400 px-4 py-2 label-caps text-[10px] tracking-[0.25em] transition-colors"
            >
              <Trash2 size={14} /> Supprimer
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
